import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getGame, setGame } from './actions';
import { selectActiveGame, selectOpponent, selectPlayer } from './selectors';
import { BoardArea, Panel, Screen, Top, TopRow, Viewport } from './styled';
import { ViewOption, ViewSwitch } from '../Controls';
import MatBoard from '../Board/MatBoard';
import TableBoard from '../Board/TableBoard';
import { MAT_VIEW, TABLE_VIEW, useBoardView } from '../Board/useBoardView';
import { isMatTurned } from '../Board/layout';
import Card from '../Card';
import Hand from '../Hand';
import PlayerBar from '../PlayerBar';
import TurnBanner from '../TurnBanner';
import DeckLook from '../Trainer/DeckLook';
import FlyingCards from '../Motion/FlyingCards';
import ActivePokemonMenu from '../ActivePokemon/ActivePokemonMenu';
import BenchContextMenu from '../Bench/BenchContextMenu';
import DeckContextMenu from '../Deck/DeckContextMenu';
import DiscardPileContextMenu from '../DiscardPile/DiscardPileContextMenu';
import RetreatContextMenu from '../RetreatButton/RetreatContextMenu';
import { Muted } from '../Controls';
import GameLog, { InlineLog, OpenLog } from '../GameLog';
import { LogButton } from '../GameLog/styled';
import useWideScreen from './useWideScreen';
import CoinToss from '../Coins/CoinToss';
import ReplayBoard from '../Replay/ReplayBoard';
import ReplayPanel from '../Replay/ReplayPanel';
import useReplay from '../Replay/useReplay';
import UndoToast from '../Undo/UndoToast';
import SwapPreview from '../Undo/SwapPreview';
import useSeenBefore from '../Replay/useSeenBefore';
import useCatchUp from '../Replay/useCatchUp';
import { CATCH_UP, REPLAY } from '../Replay/replayModes';
import { seeGame } from '../NewMoves/actions';
import DropTarget from '../Drag/DropTarget';
import { BOARD_DROP, useDragging } from '../Drag/dropTargets';

const Game = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const game = useSelector(selectActiveGame);
  const player = useSelector(selectPlayer);
  const opponent = useSelector(selectOpponent);
  const [view, setView] = useBoardView();
  const wide = useWideScreen();
  const dragging = useDragging();
  // What you'd seen before this visit: where catching up and the Replay button start.
  const seenBefore = useSeenBefore(game);
  // Replaying (REPLAY), catching up (CATCH_UP), or the live game (null). Until you choose, a game with moves you
  // haven't seen opens catching up: the board as you left it, then each new move, then the live game.
  const [chosenMode, setMode] = useState(undefined);
  const mode = chosenMode !== undefined ? chosenMode : (seenBefore && seenBefore.newMoves > 0 ? CATCH_UP : null);
  const replaying = mode !== null;
  const replay = useReplay(Number(id), seenBefore ? seenBefore.seenVersion : null, mode);
  useCatchUp(replay, mode === CATCH_UP, () => setMode(null));

  // What you've seen: each new move as catching up shows it, else the live game.
  const liveVersion = game ? game.get('version') : null;
  const catchUpVersion = mode === CATCH_UP && replay.entry ? replay.entry.version : null;
  const seenNow = mode === CATCH_UP ? catchUpVersion : liveVersion;
  useEffect(() => {
    if (seenNow !== null && seenNow !== undefined) {
      dispatch(seeGame(id, seenNow));
    }
  }, [id, seenNow, dispatch]);

  useEffect(() => {
    if (!game) {
      dispatch(setGame(null));
      dispatch(getGame(id));
    }
  }, [dispatch, id, game]);

  if (!game) {
    return <Viewport><Muted style={{ padding: 16 }}>Loading game…</Muted></Viewport>;
  }

  const playerId = player.getIn(['user', 'id']);
  const opponentId = opponent.getIn(['user', 'id']);
  const turned = isMatTurned(game, playerId);
  const userIds = [playerId, opponentId];

  return (
    <Viewport>
      <Screen>
        {/* A Trainer that goes on no Pokémon is played by dropping it anywhere on the board. */}
        <BoardArea data-anchor={BOARD_DROP}>
          <UndoToast />
          <SwapPreview />
          {dragging && dragging.dropAnywhere && <DropTarget name={BOARD_DROP} commit={dragging.dropAnywhere} />}
          {replaying && <ReplayBoard replay={replay} userId={playerId} turned={turned} />}
          {!replaying && (view === MAT_VIEW ?
            <MatBoard playerId={playerId} opponentId={opponentId} turned={turned} /> :
            <TableBoard playerId={playerId} opponentId={opponentId} />)}
        </BoardArea>

        <Top>
          <TopRow>
            <Link to="/games">‹ Games</Link>
            {!wide && <OpenLog userId={playerId} />}
            {/* While replaying, the same button goes back to the game. */}
            <LogButton onClick={() => setMode(replaying ? null : REPLAY)} aria-pressed={replaying} $on={replaying}>
              {replaying ? 'Done' : 'Replay'}
            </LogButton>
            <ViewSwitch role="group" aria-label="Board view">
              <ViewOption $on={view === MAT_VIEW} onClick={() => setView(MAT_VIEW)}>Mat</ViewOption>
              <ViewOption $on={view === TABLE_VIEW} onClick={() => setView(TABLE_VIEW)}>Table</ViewOption>
            </ViewSwitch>
          </TopRow>
          <PlayerBar userId={opponentId} />
        </Top>

        <Panel>
          {replaying && (
            <ReplayPanel
              replay={replay}
              userId={playerId}
              catchingUp={mode === CATCH_UP}
              newMoves={seenBefore ? seenBefore.newMoves : 0}
              onTakeOver={() => setMode(REPLAY)}
              onDone={() => setMode(null)}
            />
          )}
          <TurnBanner userId={playerId} />
          <PlayerBar userId={playerId} you />
          <Hand userId={playerId} />
          {wide && <InlineLog userId={playerId} />}
        </Panel>
      </Screen>
      <DeckLook userId={playerId} />
      <FlyingCards />
      <CoinToss />

      {userIds.map(userId => (
        <React.Fragment key={userId}>
          <ActivePokemonMenu userId={userId} />
          <BenchContextMenu userId={userId} />
          <DeckContextMenu userId={userId} />
          <DiscardPileContextMenu userId={userId} />
        </React.Fragment>
      ))}
      <RetreatContextMenu userId={playerId} />
      <GameLog userId={playerId} />
      <Card />
    </Viewport>
  );
};

// Each game is its own visit: going from one game to another (a notice's banner) starts afresh.
const GameVisit = () => {
  const { id } = useParams();
  return <Game key={id} />;
};

export default GameVisit;
